import { type FC, useMemo } from "react";
import { Item, Label, MenuOptionItem, Select, VStack } from "widgets";

import { useComputing } from "@/modules/workflow/hooks/useComputing.js";

import { type NfPlaybookActivity, SupportedExecutorsEnum } from "../../../graphql.js";

interface NfActivitiesConfiguratorProps {
  nfActivitiesConfig: NfPlaybookActivity[];
  setNfActivitiesConfig: (nfActivities: NfPlaybookActivity[]) => void;
}

const NfActivitiesConfigurator: FC<NfActivitiesConfiguratorProps> = (props) => {
  return (
    <div className="experiment-info-screen">
      <div className="form-group" style={{ maxWidth: "380px" }}>
        {props.nfActivitiesConfig.length === 0 && (
          <Label className="control-label">No workflow activities available for configuration</Label>
        )}
        <VStack spacing={10}>
          {props.nfActivitiesConfig.map((nfActivityConfig) => (
            <NfActivityConfigItem
              key={nfActivityConfig.name}
              nfActivityConfig={nfActivityConfig}
              onStateChanged={(editedConfig) => {
                props.setNfActivitiesConfig(
                  props.nfActivitiesConfig.map((a) => (a.name === editedConfig.name ? { ...editedConfig } : a)),
                );
              }}
            />
          ))}
        </VStack>
      </div>
    </div>
  );
};

interface NfActivityConfigItemProps {
  nfActivityConfig: NfPlaybookActivity;
  onStateChanged?: (editedConfig: NfPlaybookActivity) => void;
}

const NfActivityConfigItem: FC<NfActivityConfigItemProps> = ({ nfActivityConfig, onStateChanged }) => {
  const executors = useMemo(() => Object.values(SupportedExecutorsEnum), []);
  const { options, computingDisabledKeys, error, loading } = useComputing(executors, true, true);

  return (
    <Select
      label={nfActivityConfig.name}
      defaultSelectedKey={
        nfActivityConfig.hpcEnabled
          ? SupportedExecutorsEnum.Cluster
          : nfActivityConfig.cloudEnabled
            ? SupportedExecutorsEnum.Aws
            : SupportedExecutorsEnum.Local
      }
      onSelectionChange={(key) => {
        const config = key as SupportedExecutorsEnum | "default";
        onStateChanged?.({
          ...nfActivityConfig,
          hpcEnabled: config === SupportedExecutorsEnum.Cluster,
          cloudEnabled: config === SupportedExecutorsEnum.Aws,
        });
      }}
      items={options}
      isRequired
      disabledKeys={computingDisabledKeys}
      isDisabled={loading || !!error}
      errorMessage={error?.message}
      isInvalid={!!error}
    >
      {(item) => (
        <Item key={String(item.key)} aria-label={item.label}>
          <MenuOptionItem title={item.label} icon={item.icon} />
        </Item>
      )}
    </Select>
  );
};

export default NfActivitiesConfigurator;
